import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Colors from 'styles/colors';

interface Props {
  visible: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const UnsavedChangesModal = ({ visible, message, onConfirm, onCancel }: Props) => {
  // Trap Escape key
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible, onCancel]);

  if (!visible) return null;

  return (
    <Overlay onClick={onCancel}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <AccentBar />
        <Content>
          <Title>Unsaved changes</Title>
          <Message>{message}</Message>
          <ButtonRow>
            <StayButton onClick={onCancel}>Stay on page</StayButton>
            <LeaveButton onClick={onConfirm}>Discard &amp; leave</LeaveButton>
          </ButtonRow>
        </Content>
      </Dialog>
    </Overlay>
  );
};

export default UnsavedChangesModal;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const enter = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(7, 35, 65, 0.45);
  backdrop-filter: blur(2px);
  animation: ${fadeIn} 120ms ease-out;
`;

const Dialog = styled.div`
  width: 380px;
  max-width: calc(100vw - 40px);
  display: flex;
  background: ${Colors.pureWhite};
  border-radius: 3px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 12px 40px rgba(7, 35, 65, 0.22);
  overflow: hidden;
  animation: ${enter} 160ms cubic-bezier(0.2, 0, 0, 1);
`;

const AccentBar = styled.div`
  width: 4px;
  flex-shrink: 0;
  background: ${Colors.sahara};
`;

const Content = styled.div`
  flex: 1;
  padding: 20px 24px 18px;
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${Colors.nileBlue};
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const Message = styled.p`
  margin: 0 0 18px;
  font-size: 12.5px;
  color: ${Colors.nevada};
  line-height: 1.45;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const StayButton = styled.button`
  height: 30px;
  padding: 0 14px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  border: 1px solid ${Colors.mercury};
  background: ${Colors.pureWhite};
  color: ${Colors.pickledBlueWood};
  transition: all 100ms ease;

  &:hover {
    border-color: ${Colors.pictonBlue};
    color: ${Colors.pictonBlue};
  }

  &:active {
    background: ${Colors.whiteSmoke};
  }
`;

const LeaveButton = styled.button`
  height: 30px;
  padding: 0 14px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  border: 1px solid transparent;
  background: ${Colors.nileBlue};
  color: ${Colors.pureWhite};
  transition: all 100ms ease;

  &:hover {
    background: ${Colors.bigStone};
  }

  &:active {
    background: ${Colors.oxfordBlue};
  }
`;
